import * as React from 'react';

import { FaOptionIcon, informationIcon } from '@europrocurement/l2d-icons';
import { Alert, Box, Typography } from '@mui/material';
import { FlexyFormLabel, FlexyTextField } from '@europrocurement/flexy-components/components/atoms';
import { usePhoneFormatter } from '@europrocurement/flexy-components/hooks/usePhoneFormatter';
import { SelectItemsReader } from '../SelectItemsReader/SelectItemsReader';

export type UserAccountDetailsFormProps = {
    civilite?: number | null;
    prenom?: string;
    nom?: string;
    email?: string;
    telephone?: string;
};

/**
 * Composant d'affichage des informations personnelles de l'utilisateur
 * Gère le cas où les données de contact ne sont pas disponibles
 */
export const UserAccountDetailsForm: React.FunctionComponent<UserAccountDetailsFormProps> =
    function ({ civilite, prenom, nom, email, telephone }) {
        const phoneValue = telephone !== null ? telephone : undefined;
        const { formattedValue: formattedPhone } = usePhoneFormatter(phoneValue);

        const hasData = prenom || nom || email || telephone;

        /**
         * Rendu du message d'erreur quand les données ne sont pas disponibles
         */
        const renderContactUnavailableMessage = () => (
            <Box sx={{ p: 2 }}>
                <Typography
                    variant="h3"
                    paddingBottom="10px"
                >
                    Informations personnelles
                </Typography>

                <Alert
                    severity="warning"
                    sx={{
                        mb: 3,
                        '& .MuiAlert-message': {
                            width: '100%',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            variant="l2dbodym"
                            sx={{ mb: 1, fontWeight: 600 }}
                        >
                            Informations temporairement indisponibles
                        </Typography>
                        <Typography
                            variant="l2dbodys"
                            color="text.secondary"
                        >
                            Nous ne parvenons pas à récupérer vos informations personnelles pour le
                            moment. Cette situation peut être temporaire.
                        </Typography>
                        <Typography
                            variant="l2dbodys"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            Si le problème persiste, veuillez contacter l&apos;assistance technique.
                        </Typography>
                    </Box>
                </Alert>

                {/* Formulaire en mode "squelette" pour montrer la structure attendue */}
                <Box
                    sx={{
                        opacity: 0.6,
                        pointerEvents: 'none',
                        '& .MuiTextField-root': {
                            backgroundColor: 'action.inputProps={{ readOnly: true }}Background',
                        },
                    }}
                >
                    <Box sx={{ gap: '4px', display: 'grid', mb: 2 }}>
                        <FlexyFormLabel>
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                Civilité
                            </Typography>
                        </FlexyFormLabel>
                        <SelectItemsReader
                            mode="singleLine"
                            colInline={3}
                            gridColLength={9}
                            value={[undefined]}
                            items={[{ label: 'Non disponible', value: undefined, fullWidth: true }]}
                        />
                    </Box>

                    <Box sx={{ gap: '4px', display: 'grid', mb: 2 }}>
                        <FlexyFormLabel required>
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                Prénom
                            </Typography>
                        </FlexyFormLabel>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value=""
                            placeholder="Données non disponibles"
                            inputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box sx={{ gap: '4px', display: 'grid', mb: 2 }}>
                        <FlexyFormLabel required>
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                Nom
                            </Typography>
                        </FlexyFormLabel>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value=""
                            placeholder="Données non disponibles"
                            inputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box sx={{ gap: '4px', display: 'grid', mb: 2 }}>
                        <FlexyFormLabel required>
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                E-mail
                            </Typography>
                        </FlexyFormLabel>
                        <FlexyTextField
                            type="email"
                            variant="outlined"
                            fullWidth
                            value=""
                            placeholder="Données non disponibles"
                            inputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <FlexyFormLabel>
                            <Typography
                                variant="l2dbodys"
                                fontWeight={500}
                            >
                                Téléphone
                            </Typography>
                        </FlexyFormLabel>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value=""
                            placeholder="Données non disponibles"
                            inputProps={{ readOnly: true }}
                        />
                    </Box>
                </Box>
            </Box>
        );

        /**
         * Rendu normal du formulaire avec les données
         */
        const renderContactForm = () => (
            <Box
                p={2}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Typography
                    variant="h3"
                    paddingBottom="10px"
                >
                    Informations personnelles
                </Typography>
                <Box
                    sx={{
                        opacity: 0.6,
                        pointerEvents: 'none',
                        '& .MuiTextField-root': {
                            backgroundColor: 'action.inputProps={{ readOnly: true }}Background',
                        },
                    }}
                >
                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <Box>
                            <FlexyFormLabel>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Civilité
                                </Typography>
                            </FlexyFormLabel>
                        </Box>
                        <SelectItemsReader
                            mode="singleLine"
                            colInline={3}
                            gridColLength={9}
                            value={[
                                [101, 102].find((val) => val === civilite) ? civilite : undefined,
                            ]}
                            items={[
                                {
                                    label: 'Madame',
                                    value: 101,
                                    fullWidth: true,
                                },
                                {
                                    label: 'Monsieur',
                                    value: 102,
                                    fullWidth: true,
                                },
                                {
                                    label: 'Non précisé',
                                    value: undefined,
                                    fullWidth: true,
                                },
                            ]}
                        />
                    </Box>
                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <Box>
                            <FlexyFormLabel required>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Prénom
                                </Typography>
                            </FlexyFormLabel>
                        </Box>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value={prenom}
                            placeholder="Votre prénom..."
                            inputProps={{ readOnly: true }}
                            sx={{ gap: '8px' }}
                        />
                    </Box>
                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <Box>
                            <FlexyFormLabel required>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Nom
                                </Typography>
                            </FlexyFormLabel>
                        </Box>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value={nom}
                            placeholder="Votre nom..."
                            inputProps={{ readOnly: true }}
                            sx={{ gap: '8px' }}
                        />
                    </Box>
                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <Box>
                            <FlexyFormLabel required>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    E-mail
                                </Typography>
                            </FlexyFormLabel>
                        </Box>
                        <FlexyTextField
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={email}
                            placeholder="Votre e-mail..."
                            inputProps={{ readOnly: true }}
                            sx={{ gap: '8px' }}
                        />
                    </Box>
                    <Box sx={{ gap: '4px', display: 'grid' }}>
                        <Box>
                            <FlexyFormLabel>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Téléphone
                                </Typography>
                            </FlexyFormLabel>
                        </Box>
                        <FlexyTextField
                            type="text"
                            variant="outlined"
                            fullWidth
                            value={formattedPhone}
                            placeholder="Votre numéro de téléphone..."
                            InputProps={{
                                slotProps: {
                                    input: {
                                        readOnly: true,
                                    },
                                },
                            }}
                            sx={{ gap: '8px' }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 0.625,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    '& svg': {
                                        verticalAlign: 'baseline',
                                        display: 'block',
                                    },
                                }}
                            >
                                <FaOptionIcon
                                    {...informationIcon.props}
                                    color="text.secondary"
                                    size="sm"
                                />
                            </Box>
                            <Typography
                                variant="l2dbodys"
                                color="text.secondary"
                                sx={{
                                    lineHeight: 1.2,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                Votre numéro professionnel, partagé uniquement à vos contacts.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );

        // Retour conditionnel basé sur la disponibilité des données
        return hasData ? renderContactForm() : renderContactUnavailableMessage();
    };
